import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { ArrowLeftIcon, FileUpIcon, Loader2Icon, UploadIcon } from 'lucide-react'
import { useId, useRef, useState, type DragEvent, type SyntheticEvent } from 'react'
import { toast } from 'sonner'

import { MAX_UPLOAD_BYTES } from '@/api/documents'
import { ApiError } from '@/api/errors'
import { portalApi } from '@/api/portal'
import { FieldError, FormError } from '@/components/field-error'
import { Frame } from '@/components/frame'
import { PageHeader } from '@/components/page-header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { msg } from '@/i18n'
import { formatBytes } from '@/lib/format'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/portal/documents_/nouveau')({
  component: PortalDocumentUploadPage,
})

const NONE = '__aucun__'

/** Le refus du dépôt, dit au client ; `invalid` : il faut rechoisir le fichier. */
function uploadProblem(error: unknown): { text: string; invalid: boolean } | null {
  if (!error) return null
  const t = msg().portalDocuments.upload
  if (!(error instanceof ApiError)) return { text: t.failed, invalid: false }
  if (error.reason === 'attachment_invalid') return { text: t.attachmentInvalid, invalid: true }
  if (error.status === 404) return { text: t.notVisible, invalid: false }
  if (error.status === 429) return { text: t.quota, invalid: false }
  return { text: error.message, invalid: false }
}

/**
 * « + Nouvelle demande › Document » (#346, contrat 1.75.0) : le client dépose
 * un fichier. Le fichier part d'abord en pièce en attente (`/portal/uploads`),
 * puis devient un document (`POST /portal/documents`) avec son nom, sa
 * description, son projet et son dossier. Un refus qui ne tient pas à la pièce
 * (projet ou dossier disparu, quota) garde la pièce : on la réutilise.
 */
function PortalDocumentUploadPage() {
  const t = msg().portalDocuments.upload
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const ids = {
    file: useId(),
    name: useId(),
    description: useId(),
    project: useId(),
    folder: useId(),
  }
  const input = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [uploadId, setUploadId] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [project, setProject] = useState(NONE)
  const [folder, setFolder] = useState(NONE)
  const [touched, setTouched] = useState(false)
  const [dragging, setDragging] = useState(false)

  const projects = useQuery({
    queryKey: ['portal', 'projects'],
    queryFn: () => portalApi.projects(),
  })
  const folders = useQuery({ queryKey: ['portal', 'folders'], queryFn: () => portalApi.folders() })

  const deposit = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('no file')
      const pending = uploadId ?? (await portalApi.upload(file)).id
      setUploadId(pending)
      return portalApi.createDocument({
        upload_id: pending,
        name: name.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        project_id: project === NONE ? null : project,
        folder_id: folder === NONE ? null : folder,
      })
    },
    onSuccess: (document) => {
      void queryClient.invalidateQueries({ queryKey: ['portal'] })
      toast.success(msg().portalDocuments.upload.done)
      void navigate({ to: '/portal/documents', search: { document: document.id } })
    },
    onError: (error) => {
      const problem = uploadProblem(error)
      if (problem?.invalid) setUploadId(null)
      if (error instanceof ApiError && error.status === 404) {
        void queryClient.invalidateQueries({ queryKey: ['portal', 'projects'] })
        void queryClient.invalidateQueries({ queryKey: ['portal', 'folders'] })
      }
    },
  })

  const choose = (next: File | undefined) => {
    if (!next) return
    setFile(next)
    setUploadId(null)
    deposit.reset()
    if (name.trim() === '' || name === file?.name) setName(next.name)
  }
  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDragging(false)
    choose(event.dataTransfer.files[0])
  }

  const tooBig = file !== null && file.size > MAX_UPLOAD_BYTES
  const fileError = touched
    ? !file
      ? t.fileRequired
      : tooBig
        ? t.tooBig(formatBytes(MAX_UPLOAD_BYTES))
        : undefined
    : undefined
  const nameError =
    touched && (name.trim() === '' || name.trim().length > 200) ? t.nameRequired : undefined
  const problem = uploadProblem(deposit.error)

  const submit = (event: SyntheticEvent) => {
    event.preventDefault()
    setTouched(true)
    if (!file || tooBig || name.trim() === '' || name.trim().length > 200) return
    deposit.mutate()
  }

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow={
          <nav className="px-2">
            <Link to="/portal/documents" className="inline-flex items-center gap-1">
              <ArrowLeftIcon className="size-3.5" aria-hidden="true" />
              {t.back}
            </Link>
          </nav>
        }
        title={t.title}
        documentTitle={t.title}
      />
      <Frame>
        <form
          onSubmit={submit}
          noValidate
          className="max-w-2xl space-y-5"
          data-testid="deposer-document"
        >
          <p className="text-sm text-muted-foreground">{t.intro}</p>
          <FormError>{problem?.text ?? null}</FormError>

          <div className="space-y-2">
            <div
              className={cn(
                'flex flex-col items-center gap-3 rounded-lg border-2 border-dashed px-4 py-8 text-center transition-colors',
                dragging ? 'border-primary bg-primary/5' : 'border-border',
                fileError && 'border-destructive',
              )}
              data-testid="zone-depot"
              onDragOver={(event) => {
                event.preventDefault()
                setDragging(true)
              }}
              onDragLeave={() => {
                setDragging(false)
              }}
              onDrop={onDrop}
            >
              <FileUpIcon className="size-8 text-muted-foreground" aria-hidden="true" />
              {file ? (
                <p className="text-sm">
                  <span className="font-medium">{file.name}</span>{' '}
                  <span className="text-muted-foreground">· {formatBytes(file.size)}</span>
                </p>
              ) : (
                <p className="text-sm font-medium">{t.drop}</p>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  input.current?.click()
                }}
              >
                <UploadIcon />
                {file ? t.change : t.choose}
              </Button>
              <input
                ref={input}
                id={ids.file}
                type="file"
                className="sr-only"
                aria-label={t.choose}
                onChange={(event) => {
                  choose(event.target.files?.[0])
                  event.target.value = ''
                }}
              />
              <p className="text-xs text-muted-foreground">
                {t.dropHint(formatBytes(MAX_UPLOAD_BYTES))}
              </p>
            </div>
            <FieldError>{fileError}</FieldError>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={ids.name}>{t.name}</Label>
            <Input
              id={ids.name}
              value={name}
              maxLength={200}
              aria-invalid={!!nameError}
              onChange={(event) => {
                setName(event.target.value)
              }}
            />
            <FieldError>{nameError}</FieldError>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={ids.description}>{t.description}</Label>
            <Textarea
              id={ids.description}
              rows={3}
              maxLength={2000}
              value={description}
              placeholder={t.descriptionPlaceholder}
              onChange={(event) => {
                setDescription(event.target.value)
              }}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {(projects.data?.length ?? 0) > 0 && (
              <div className="space-y-1.5">
                <Label htmlFor={ids.project}>{t.project}</Label>
                <Select value={project} onValueChange={setProject}>
                  <SelectTrigger id={ids.project} className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>{t.noProject}</SelectItem>
                    {(projects.data ?? []).map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {(folders.data?.length ?? 0) > 0 && (
              <div className="space-y-1.5">
                <Label htmlFor={ids.folder}>{t.folder}</Label>
                <Select value={folder} onValueChange={setFolder}>
                  <SelectTrigger id={ids.folder} className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>{t.root}</SelectItem>
                    {(folders.data ?? []).map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {item.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={deposit.isPending}>
              {deposit.isPending ? <Loader2Icon className="animate-spin" /> : <UploadIcon />}
              {t.submit}
            </Button>
          </div>
        </form>
      </Frame>
    </div>
  )
}
